// src/lib/supabase.ts
import { createClient } from '@supabase/supabase-js'
import type { Database } from '@/types'
import { getSupabaseCredentials } from '@/lib/supabaseCredentials'

// Быстрая проверка JWT exp прямо в fetch-interceptor.
// Отдельная от supabaseSessionRecovery.isTokenExpired чтобы избежать циклического импорта.
function isTokenExpiredQuick(token: string): boolean {
  try {
    const part = token.split('.')[1]
    if (!part) return true
    const { exp } = JSON.parse(atob(part)) as { exp?: number }
    return typeof exp === 'number' && exp * 1000 <= Date.now()
  } catch {
    return true
  }
}

export interface SupabaseFetchInfo {
  url: string
  status: number | null
  durationMs: number
  error: unknown
  response: Response | null
}

// Наблюдатель за каждым запросом к Supabase (журнал ошибок клиента, src/lib/clientErrorReporter.ts).
// Регистрируется снаружи, чтобы этот модуль не зависел от журнала.
let fetchObserver: ((info: SupabaseFetchInfo) => void) | null = null

export function setSupabaseFetchObserver(observer: ((info: SupabaseFetchInfo) => void) | null): void {
  fetchObserver = observer
}

function notifyFetchObserver(info: SupabaseFetchInfo): void {
  try {
    fetchObserver?.(info)
  } catch {
    // наблюдатель никогда не ломает запрос
  }
}

// Ленивая инициализация, чтобы не падать, если env не заданы (покажем в UI понятную ошибку).
let _client: ReturnType<typeof createClient<Database>> | null = null

export function getSupabase() {
  if (_client) return _client
  
  const credentials = getSupabaseCredentials()
  
  if (!credentials.url || !credentials.apiKey) {
    const environment = String(import.meta.env.ENVIRONMENT || 'dev').toLowerCase()
    const prefix = environment === 'prod' ? 'PROD_' : 'DEV_'
    throw new Error(
      `Missing Supabase credentials. Configure ${prefix}SUPABASE_URL and ${prefix}SUPABASE_PUBLISHABLE_KEY (or legacy ANON_KEY) in .env file`
    )
  }
  
  // Показываем в консоли, к какому окружению подключаемся (только в dev режиме)
  if (import.meta.env.DEV) {
    const env = credentials.isProd ? '🚨 PROD' : '🔧 DEV'
    console.log(`[Supabase] Connected to ${env} environment:`, credentials.url)
  }
  
  _client = createClient<Database>(credentials.url, credentials.apiKey, {
    auth: {
      autoRefreshToken: false,   // No Supabase Auth session to refresh
      persistSession: true,      // Keep for transition: /email-verified still uses Supabase session
      detectSessionInUrl: true   // Keep for transition: email verification callback needs URL parsing
    },
    global: {
      fetch: (input, init) => {
        const requestUrl =
          typeof input === 'string'
            ? input
            : input instanceof Request
              ? input.url
              : ''
        const isSupabaseAuthEndpoint = requestUrl.includes('/auth/v1/')

        const headers = new Headers(
          init?.headers ?? (input instanceof Request ? input.headers : undefined)
        )
        // Inject custom JWT from tg-auth so that PostgREST sees authenticated identity.
        // supabase-js may prefill Authorization with anon key; in that case we must override it.
        // Do not override Authorization for Supabase Auth endpoints (PKCE/session exchange).
        // Skip injection if the token is expired — let withAuthRecovery handle refresh.
        const token = localStorage.getItem('auth_token')
        if (token && !isSupabaseAuthEndpoint && !isTokenExpiredQuick(token)) {
          const currentAuth = headers.get('Authorization')
          const anonAuth = `Bearer ${credentials.apiKey}`
          if (!currentAuth || currentAuth === anonAuth) {
            headers.set('Authorization', `Bearer ${token}`)
          }
        }
        // ⚠️ Authorization не вырезаем ни для какого формата ключа: PostgREST берёт
        // роль именно из него, а один лишь apikey даёт 200 и пустой ответ (замер
        // PROD 11.09.2026). Ключ проекта в Authorization шлюз принимает.
        if (!headers.get('apikey')) {
          headers.set('apikey', credentials.apiKey)
        }
        if (!headers.get('Authorization')) {
          headers.set('Authorization', `Bearer ${credentials.apiKey}`)
        }
        const startedAt = performance.now()
        return fetch(input, { ...init, headers, credentials: 'omit' }).then(
          (response) => {
            notifyFetchObserver({
              url: requestUrl,
              status: response.status,
              durationMs: performance.now() - startedAt,
              error: null,
              response,
            })
            return response
          },
          (error: unknown) => {
            notifyFetchObserver({
              url: requestUrl,
              status: null,
              durationMs: performance.now() - startedAt,
              error,
              response: null,
            })
            throw error
          }
        )
      }
    }
  })
  return _client
}

// Оставляем удобный алиас, но через getter (чтобы типы были привычные)
export const supabase = getSupabase()
